import Clear from "@mui/icons-material/Clear";
import { Button, Checkbox, Stack, Typography } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useDeepCompareMemo } from "@react-hookz/web";
import { useCallback, useMemo } from "react";
import { usePermissionOverrides } from "../../../../../../server/auth/use-permission-overrides";
import { usePermissions } from "../../../../../../server/auth/use-permissions";

import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import type { FC } from "react";
import "./debug.css";

interface Row {
  id: "ce_admin" | "super_admin";
  permission: string;
  enabled: boolean;
  source: string;
}

export const PermissionOverridesGrid: FC = () => {
  const permissions = usePermissions();
  const {
    overrides: { ce_admin, super_admin },
    setOverride,
    clearOverrides,
  } = usePermissionOverrides();

  const togglePermission = useCallback(
    (permission: "ce_admin" | "super_admin") => {
      setOverride(permission, !permissions[permission]);
    },
    [permissions, setOverride],
  );

  const rows: Row[] = useDeepCompareMemo(
    () => [
      {
        id: "ce_admin",
        permission: "CE Admin",
        enabled: permissions.ce_admin,
        source: ce_admin !== undefined ? "Override" : "Default",
      },
      {
        id: "super_admin",
        permission: "Super Admin",
        enabled: permissions.super_admin,
        source: super_admin !== undefined ? "Override" : "Default",
      },
    ],
    [permissions, ce_admin, super_admin],
  );

  const columns = useMemo(() => {
    const cols: GridColDef<Row>[] = [
      { field: "permission", headerName: "Permission", flex: 1 },
      {
        field: "enabled",
        headerName: "Enabled",
        flex: 0.5,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<Row>) => (
          <Checkbox checked={row.enabled} onChange={() => togglePermission(row.id)} size="small" />
        ),
      },
      { field: "source", headerName: "Source", flex: 0.7 },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: ({ row }: GridRenderCellParams<Row>) =>
          row.source === "Override" ? (
            <Button
              size="small"
              startIcon={<Clear />}
              onClick={() => setOverride(row.id, undefined)}
              data-analytics-id={`debug-clear-override-${row.id}`}
            >
              Clear
            </Button>
          ) : null,
      },
    ];

    return cols;
  }, [setOverride, togglePermission]);

  return (
    <Stack spacing={1} direction="column">
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Stack direction="column" spacing={1}>
          <Typography variant="h6">Permissions</Typography>
          <Typography variant="caption" color="textSecondary">
            Override permissions for testing. Note that any changes will only be on the frontend, it
            will still be able to access data from the backend.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={clearOverrides} data-analytics-id="debug-clear-overrides">
            Clear Permissions Overrides
          </Button>
        </Stack>
      </Stack>

      <DataGridPro
        rows={rows}
        getRowClassName={(params) => (params.row.source === "Override" ? "overridden-flag" : "")}
        columns={columns}
        disableRowSelectionOnClick
        initialState={{
          sorting: { sortModel: [{ field: "flag", sort: "asc" }] },
        }}
      />
    </Stack>
  );
};
